// Сегменты флота на карте: свой / национальный / иностранный.
//
// Три НЕЗАВИСИМЫХ фильтра-множества: каждая кнопка показывает и скрывает свой
// слой живых AIS-позиций сама по себе. Источник у всех один — таблица ais_vessel
// (коллектор + enrichment). «Свой» и «национальный» больше НЕ спорят: раньше
// запрос «весь флот» включал свои суда и оба слоя рисовали их дважды; теперь
// сегмент определяет scope запроса (own|nat|foreign), суда каждого scope
// дедуплицируются на фронте по mmsi, поэтому пересечение безопасно.
//
// СРЕДНИЙ СЕГМЕНТ ЗОВЁТСЯ `nat`, А НЕ `ru`. Кнопка подписана «Нацфлот» с тех
// пор, как витрин стало больше одной, а идентификатор оставался от времён
// единственного домена. Пока сегмент зовут по стране, «национальный» и
// «российский» так и будут расходиться: подпись говорит одно, ссылка другое, а
// какой флаг попадёт в выборку — решает бэкенд (`NATIONAL_FLAG`), и сегодня это
// по-прежнему РФ на любой витрине. Имя `nat` этого не чинит, но и не мешает
// починить: страну остаётся дописать домену, а не двадцати местам фронта.

export type FleetSegment = 'own' | 'nat' | 'foreign'

export const FLEET_SEGMENTS: { id: FleetSegment; labelKey: string }[] = [
  { id: 'own', labelKey: 'models.fleet.own' },
  { id: 'nat', labelKey: 'models.fleet.national' },
  { id: 'foreign', labelKey: 'models.fleet.foreign' },
]

/** Позиции есть у всех трёх сегментов: живой AIS-стрим (ais_vessel) покрывает и
 *  иностранный флот — раньше кнопка «Инофлот» была заглушена, потому что точек
 *  для него не было. Теперь есть, и она равноправна. */
export function isForeignAvailable(): boolean {
  return true
}

/** Независимый мультивыбор: сегмент добавляется/убирается, остальные не трогаем. */
export function toggleFleet(current: FleetSegment[], segment: FleetSegment): FleetSegment[] {
  if (current.includes(segment)) return current.filter(s => s !== segment)
  return canonical([...current, segment])
}

/** Backend-scope выбранного сегмента для GET /fleet/vessels: own→own, nat→nat,
 *  foreign→foreign. Один включённый слой = один запрос своего scope; результаты
 *  сливаются с дедупликацией по mmsi (см. ModelsPage). */
export function backendScopeFor(segment: FleetSegment): 'own' | 'nat' | 'foreign' {
  return segment
}

const ORDER: FleetSegment[] = FLEET_SEGMENTS.map(s => s.id)

function canonical(segments: FleetSegment[]): FleetSegment[] {
  return ORDER.filter(id => segments.includes(id))
}

export function serializeFleet(segments: FleetSegment[]): string {
  return canonical(segments).join(',') || 'off'
}

/** Прежние имена сегментов. `ru` звался по стране, а не по смыслу; короткие
 *  ссылки живут шесть часов, сохранённые виды — сколько угодно, и в них лежит
 *  старое имя. Пишем в ссылку только новое, читаем оба. */
const ALIASES: Record<string, FleetSegment> = { ru: 'nat' }

/** Разбор параметра URL. null — параметра нет (оставить умолчание).
 *
 *  Понимает и прежние значения off|own|all: старые ссылки должны открываться. */
export function parseFleet(raw: string | null): FleetSegment[] | null {
  if (raw === null) return null
  if (raw === 'off' || raw === '') return []
  if (raw === 'all') return ['nat'] // «весь флот» прежних ссылок
  const parts = raw.split(',').map(p => ALIASES[p] ?? p)
  return canonical(parts.filter((p): p is FleetSegment => ORDER.includes(p as FleetSegment)))
}

/** Каким сегментом карты показывается судно с этим флагом.
 *
 *  Нужно строке поиска: выбрать судно при выключенном слое значит улететь в
 *  пустое море — сегмент включаем сами. «Свой» здесь не различаем: он
 *  подмножество РФ (дедуп по mmsi на фронте), и судно всё равно окажется видно.
 */
export function segmentForFlag(flag: string | null | undefined): FleetSegment {
  return flag === 'RUS' ? 'nat' : 'foreign'
}
